import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui';

const ACCENT = '#0085CA';
const NAVY   = '#003D6B';
const LIGHT  = '#EEF6FC';

const ALL_MODULES = [
  { id: 'tickets',      label: 'Tickets / HelpDesk',     core: true  },
  { id: 'agentes',      label: 'Gestión de agentes',      core: true  },
  { id: 'calendario',   label: 'Calendario',              core: false },
  { id: 'equipos',      label: 'Inventario equipos',      core: false },
  { id: 'sla',          label: 'SLA',                     core: false },
  { id: 'encuestas',    label: 'Encuestas',               core: false },
  { id: 'clientes',     label: 'Clientes VIP',            core: false },
  { id: 'derivaciones', label: 'Derivaciones',            core: false },
  { id: 'extensiones',  label: 'Extensiones telefónicas', core: false },
  { id: 'reservas',     label: 'Reserva de espacios',     core: false },
  { id: 'informes',     label: 'Informes / Facturación',  core: false },
];

const LANGS = [
  { value: 'es', label: '🇪🇸 Español' },
  { value: 'pt', label: '🇦🇴 Português' },
  { value: 'en', label: '🇬🇧 English' },
  { value: 'fr', label: '🇫🇷 Français' },
];

function tok() { return localStorage.getItem('hd_token'); }
function apiFetch(path, opts = {}) {
  return fetch(`/api/superadmin${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}`, ...(opts.headers || {}) },
  }).then(r => r.json());
}

function MIcon({ name, size = 18, style = {} }) {
  return <span className="material-symbols-rounded" style={{ fontSize: size, lineHeight: 1, verticalAlign: 'middle', ...style }}>{name}</span>;
}

const inputSt = { padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 7, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', background: '#fff' };
const labelSt = { fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: .4, display: 'block', marginBottom: 4 };

function Field({ label, children }) {
  return <div><label style={labelSt}>{label}</label>{children}</div>;
}

function Section({ title, icon, children }) {
  return (
    <div style={{ border: '1px solid #F3F4F6', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: '#F9FAFB', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
        <MIcon name={icon} size={14} style={{ color: '#6B7280' }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: .4 }}>{title}</span>
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

export default function SuperAdminPage() {
  const { agent } = useAuth();
  const navigate  = useNavigate();
  const [tenants,  setTenants]  = useState([]);
  const [stats,    setStats]    = useState(null);
  const [view,     setView]     = useState('list');
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [msg,      setMsg]      = useState('');

  useEffect(() => {
    if (agent?.role !== 'superadmin') { navigate('/backdesk'); return; }
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const [t, s] = await Promise.all([apiFetch('/tenants'), apiFetch('/stats')]);
    setTenants(Array.isArray(t) ? t : []);
    setStats(s);
    setLoading(false);
  }

  async function openDetail(tenant, nextView = 'edit') {
    const detail = await apiFetch(`/tenants/${tenant.id}`);
    setSelected(detail);
    setView(nextView);
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F5F6F8', fontFamily: "'Inter','Arial',sans-serif" }}>
      {/* AppBar */}
      <header style={{ height: 56, background: '#fff', borderBottom: `2px solid ${ACCENT}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MIcon name="admin_panel_settings" size={22} style={{ color: ACCENT }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>SuperAdmin — FUNIBER</span>
          {view !== 'list' && <>
            <span style={{ color: '#D1D5DB' }}>/</span>
            <button onClick={() => { setView('list'); setSelected(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: 13 }}>Tenants</button>
            {selected && <><span style={{ color: '#D1D5DB' }}>/</span><span style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>{selected.name || 'Nuevo'}</span></>}
          </>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#6B7280' }}>{agent?.full_name}</span>
          <Button variant="secondary" onClick={() => navigate('/backdesk')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <MIcon name="arrow_back" size={13} />Volver al BackDesk
          </Button>
        </div>
      </header>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        {msg && (
          <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#065F46', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><MIcon name="check_circle" size={14} style={{ marginRight: 6 }} />{msg}</span>
            <button onClick={() => setMsg('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontSize: 16 }}>×</button>
          </div>
        )}

        {/* Lista */}
        {view === 'list' && (
          <>
            {stats && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                {[
                  { icon: 'domain', label: 'Total tenants', value: stats.total_tenants, color: ACCENT },
                  { icon: 'check_circle', label: 'Activos', value: stats.active_tenants, color: '#059669' },
                  { icon: 'cancel', label: 'Inactivos', value: (stats.total_tenants - stats.active_tenants), color: '#DC2626' },
                ].map(s => (
                  <div key={s.label} style={{ background: '#fff', borderRadius: 10, padding: '14px 20px', flex: 1, borderTop: `3px solid ${s.color}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <MIcon name={s.icon} size={14} style={{ color: s.color }} />
                      <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .4 }}>{s.label}</span>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: NAVY }}>{s.value}</div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>
                  <MIcon name="domain" size={15} style={{ marginRight: 8, color: '#9CA3AF' }} />Tenants registrados
                </div>
                <Button variant="primary" onClick={() => { setSelected(null); setView('create'); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MIcon name="add" size={16} />Nuevo tenant
                </Button>
              </div>

              {loading ? (
                <div style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF' }}>Cargando…</div>
              ) : tenants.length === 0 ? (
                <div style={{ padding: '72px 0', textAlign: 'center', color: '#9CA3AF' }}>
                  <MIcon name="domain" size={48} style={{ display: 'block', margin: '0 auto 12px', color: '#E5E7EB' }} />
                  <div style={{ fontSize: 15, fontWeight: 600 }}>Sin tenants registrados</div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>Crea el primero para empezar</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>{['', 'Dominio', 'Nombre', 'Idioma', 'Módulos', 'Estado', 'Acciones'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid #F3F4F6' }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {tenants.map((t, i) => {
                      const activeMods = (t.modules || []).filter(m => m.active).length;
                      return (
                        <tr key={t.id} style={{ background: i % 2 === 0 ? '#fff' : '#F9FAFB' }}>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ width: 36, height: 36, borderRadius: 8, background: t.primary_color || ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{(t.short || t.name || '?').slice(0,2).toUpperCase()}</span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t.domain}</div>
                            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{t.db_path?.split('/').pop()}</div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontSize: 13, color: '#374151' }}>{t.name}</div>
                            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{t.app_name}</div>
                          </td>
                          <td style={{ padding: '12px 14px', fontSize: 13, color: '#374151' }}>{LANGS.find(l => l.value === t.lang)?.label || t.lang}</td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ background: LIGHT, color: ACCENT, padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>{activeMods}/{ALL_MODULES.length}</span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: t.active ? '#D1FAE5' : '#FEE2E2', color: t.active ? '#065F46' : '#B91C1C' }}>
                              {t.active ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              {[
                                { icon: 'edit',      title: 'Editar branding', view: 'edit' },
                                { icon: 'extension', title: 'Gestionar módulos', view: 'modules' },
                                { icon: 'key',       title: 'Credenciales OAuth/SMTP', view: 'credentials' },
                                { icon: 'open_in_new', title: 'Acceder al tenant', view: 'open' },
                              ].map(btn => (
                                <button key={btn.view} title={btn.title} onClick={() => btn.view === 'open' ? window.open(`${window.location.protocol}//${t.domain}/backdesk`, '_blank') : openDetail(t, btn.view)}
                                  style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #E5E7EB', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#374151' }}>
                                  <MIcon name={btn.icon} size={14} />
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Crear / Editar */}
        {(view === 'create' || view === 'edit') && (
          <TenantForm tenant={selected} onCancel={() => { setView('list'); setSelected(null); }}
            onSave={async (data) => {
              if (selected) {
                await apiFetch(`/tenants/${selected.id}`, { method: 'PUT', body: JSON.stringify(data) });
                setMsg(`Tenant "${data.name}" actualizado`);
              } else {
                await apiFetch('/tenants', { method: 'POST', body: JSON.stringify(data) });
                setMsg(`Tenant "${data.name}" creado correctamente`);
              }
              loadData(); setView('list'); setSelected(null);
            }}
          />
        )}

        {/* Módulos */}
        {view === 'modules' && selected && (
          <ModulesEditor tenant={selected} onCancel={() => { setView('list'); setSelected(null); }}
            onSave={async (modules) => {
              await apiFetch(`/tenants/${selected.id}/modules`, { method: 'PUT', body: JSON.stringify({ modules }) });
              setMsg('Módulos actualizados'); loadData(); setView('list'); setSelected(null);
            }}
          />
        )}

        {/* Credenciales */}
        {view === 'credentials' && selected && (
          <CredentialsEditor tenant={selected} onCancel={() => { setView('list'); setSelected(null); }}
            onSave={async (data) => {
              await apiFetch(`/tenants/${selected.id}/config`, { method: 'PUT', body: JSON.stringify(data) });
              setMsg('Credenciales guardadas correctamente'); setView('list'); setSelected(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function TenantForm({ tenant, onSave, onCancel }) {
  const [form, setForm] = useState({
    domain: tenant?.domain || '', name: tenant?.name || '', short: tenant?.short || '',
    app_name: tenant?.app_name || '', lang: tenant?.lang || 'es',
    primary_color: tenant?.primary_color || '#0085CA', accent_color: tenant?.accent_color || '#003D6B',
    logo_url: tenant?.logo_url || '', support_email: tenant?.support_email || '',
    country: tenant?.country || 'ES', active: tenant?.active ?? 1,
  });
  const [saving, setSaving] = useState(false);
  const ff = v => setForm(p => ({ ...p, ...v }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 4 }}>
          <MIcon name="domain" size={15} style={{ marginRight: 8, color: '#9CA3AF' }} />
          {tenant ? 'Editar tenant' : 'Nuevo tenant'}
        </div>
        <Field label="Dominio *">
          <input value={form.domain} onChange={e => ff({ domain: e.target.value })} placeholder="ej. unic.co.ao"
            disabled={!!tenant} style={{ ...inputSt, background: tenant ? '#F9FAFB' : '#fff', color: tenant ? '#9CA3AF' : '#111' }} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Nombre completo *">
            <input value={form.name} onChange={e => ff({ name: e.target.value })} placeholder="Universidade Internacional do Cuanza" style={inputSt} />
          </Field>
          <Field label="Nombre corto">
            <input value={form.short} onChange={e => ff({ short: e.target.value })} placeholder="UNIC" style={inputSt} />
          </Field>
        </div>
        <Field label="Nombre de la aplicación">
          <input value={form.app_name} onChange={e => ff({ app_name: e.target.value })} placeholder="Suporte TI — UNIC" style={inputSt} />
        </Field>
        <Field label="Email de soporte">
          <input type="email" value={form.support_email} onChange={e => ff({ support_email: e.target.value })} placeholder="suporte@unic.co.ao" style={inputSt} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Idioma">
            <select value={form.lang} onChange={e => ff({ lang: e.target.value })} style={{ ...inputSt, cursor: 'pointer' }}>
              {LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </Field>
          <Field label="País">
            <select value={form.country} onChange={e => ff({ country: e.target.value })} style={{ ...inputSt, cursor: 'pointer' }}>
              {[['ES','🇪🇸 España'],['AO','🇦🇴 Angola'],['DO','🇩🇴 R. Dominicana'],['PE','🇵🇪 Perú'],['MX','🇲🇽 México'],['CO','🇨🇴 Colombia'],['BR','🇧🇷 Brasil']].map(([v,l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </Field>
        </div>
        {tenant && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', padding: '10px', borderRadius: 8, border: `1px solid ${form.active ? '#6EE7B7' : '#FECACA'}`, background: form.active ? '#F0FDF4' : '#FEF2F2' }}>
            <input type="checkbox" checked={!!form.active} onChange={e => ff({ active: e.target.checked ? 1 : 0 })} style={{ accentColor: '#059669', width: 15, height: 15 }} />
            Tenant {form.active ? 'activo' : 'inactivo'}
          </label>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
            <MIcon name="palette" size={14} style={{ marginRight: 8, color: '#9CA3AF' }} />Branding
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Color primario">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="color" value={form.primary_color} onChange={e => ff({ primary_color: e.target.value })} style={{ width: 40, height: 36, border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                <input value={form.primary_color} onChange={e => ff({ primary_color: e.target.value })} style={{ ...inputSt, flex: 1, fontSize: 12 }} />
              </div>
            </Field>
            <Field label="Color acento">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="color" value={form.accent_color} onChange={e => ff({ accent_color: e.target.value })} style={{ width: 40, height: 36, border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                <input value={form.accent_color} onChange={e => ff({ accent_color: e.target.value })} style={{ ...inputSt, flex: 1, fontSize: 12 }} />
              </div>
            </Field>
          </div>
          <Field label="URL del logo">
            <input value={form.logo_url} onChange={e => ff({ logo_url: e.target.value })} placeholder="https://... o /logo.svg" style={inputSt} />
          </Field>

          {/* Preview AppBar */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 6 }}>Vista previa</div>
            <div style={{ borderRadius: 8, border: '1px solid #F3F4F6', overflow: 'hidden' }}>
              <div style={{ height: 44, background: '#fff', borderBottom: `2px solid ${form.primary_color}`, display: 'flex', alignItems: 'center', padding: '0 14px', gap: 10 }}>
                {form.logo_url ? (
                  <img src={form.logo_url} alt="" style={{ height: 26, objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
                ) : (
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: form.primary_color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>{(form.short||form.name||'?').slice(0,2).toUpperCase()}</span>
                  </div>
                )}
                <div style={{ width: 1, height: 20, background: '#E5E7EB' }} />
                <span style={{ fontSize: 13, color: '#374151' }}>{form.app_name || 'HelpDesk TI'}</span>
              </div>
              <div style={{ background: '#F9FAFB', padding: '6px 14px', fontSize: 10, color: '#9CA3AF', textAlign: 'center' }}>AppBar</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={onCancel} style={{ flex: 1 }}>Cancelar</Button>
          <Button variant="primary" onClick={async () => { setSaving(true); await onSave(form); setSaving(false); }}
            disabled={saving || !form.domain || !form.name} style={{ flex: 2 }}>
            {saving ? 'Guardando…' : tenant ? 'Guardar cambios' : 'Crear tenant'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ModulesEditor({ tenant, onSave, onCancel }) {
  const [modules, setModules] = useState(
    ALL_MODULES.map(m => ({ ...m, active: !!(tenant.modules || []).find(tm => tm.module === m.id && tm.active) || m.core }))
  );
  const [saving, setSaving] = useState(false);

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}><MIcon name="extension" size={15} style={{ marginRight: 8, color: '#9CA3AF' }} />Módulos — {tenant.name}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Los módulos core no se pueden desactivar</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {modules.map(m => (
            <label key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 8, border: `1px solid ${m.active ? ACCENT + '33' : '#F3F4F6'}`, background: m.active ? LIGHT : '#FAFAFA', cursor: m.core ? 'default' : 'pointer' }}
              onClick={() => !m.core && setModules(prev => prev.map(x => x.id === m.id ? { ...x, active: !x.active } : x))}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <MIcon name={m.active ? 'check_circle' : 'radio_button_unchecked'} size={18} style={{ color: m.active ? '#059669' : '#D1D5DB' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{m.label}</div>
                  {m.core && <div style={{ fontSize: 11, color: '#9CA3AF' }}>Módulo core — siempre activo</div>}
                </div>
              </div>
              <input type="checkbox" checked={m.active} readOnly disabled={m.core} style={{ accentColor: ACCENT, width: 16, height: 16 }} />
            </label>
          ))}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
          <Button variant="primary" onClick={async () => { setSaving(true); await onSave(modules.map(m => ({ module: m.id, active: m.active ? 1 : 0 }))); setSaving(false); }}
            disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar módulos'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CredentialsEditor({ tenant, onSave, onCancel }) {
  const cfg = tenant.config || {};
  const [form, setForm] = useState({
    google_client_id: '', google_client_secret: '',
    google_allowed_domains: cfg.google_allowed_domains || '',
    smtp_host: cfg.smtp_host || '', smtp_user: cfg.smtp_user || '', smtp_pass: '',
    trusted_ips: cfg.trusted_ips || '',
  });
  const [saving, setSaving] = useState(false);
  const ff = v => setForm(p => ({ ...p, ...v }));

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}><MIcon name="key" size={15} style={{ marginRight: 8, color: '#9CA3AF' }} />Credenciales — {tenant.name}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Cifradas con AES-256. Campos vacíos mantienen el valor actual.</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

          <Section title="Google OAuth" icon="google">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field label={`Client ID ${cfg.google_client_id_set ? '(✓ ya configurado)' : '(no configurado)'}`}>
                <input value={form.google_client_id} onChange={e => ff({ google_client_id: e.target.value })}
                  placeholder={cfg.google_client_id_set ? 'Dejar vacío para mantener' : 'Pegar Client ID de Google Cloud Console'}
                  style={inputSt} />
              </Field>
              <Field label={`Client Secret ${cfg.google_client_secret_set ? '(✓ ya configurado)' : '(no configurado)'}`}>
                <input type="password" value={form.google_client_secret} onChange={e => ff({ google_client_secret: e.target.value })}
                  placeholder={cfg.google_client_secret_set ? 'Dejar vacío para mantener' : 'Pegar Client Secret'}
                  style={inputSt} />
              </Field>
              <Field label="Dominios permitidos (separados por coma)">
                <input value={form.google_allowed_domains} onChange={e => ff({ google_allowed_domains: e.target.value })}
                  placeholder="ej. unic.co.ao,funiber.org" style={inputSt} />
              </Field>
              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 7, padding: '10px 14px', fontSize: 12, color: '#92400E' }}>
                <MIcon name="info" size={13} style={{ marginRight: 6 }} />
                Registra este callback en Google Cloud Console:<br />
                <code style={{ fontFamily: 'monospace', background: '#FEF3C7', padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>
                  https://{tenant.domain}/api/auth/google/callback
                </code>
              </div>
            </div>
          </Section>

          <Section title="SMTP (correo)" icon="mail">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Servidor SMTP"><input value={form.smtp_host} onChange={e => ff({ smtp_host: e.target.value })} placeholder="smtp.gmail.com" style={inputSt} /></Field>
              <Field label="Usuario SMTP"><input value={form.smtp_user} onChange={e => ff({ smtp_user: e.target.value })} placeholder="noreply@unic.co.ao" style={inputSt} /></Field>
              <div style={{ gridColumn: '1/-1' }}>
                <Field label={`Contraseña SMTP ${cfg.smtp_pass_set ? '(✓ ya configurada)' : '(no configurada)'}`}>
                  <input type="password" value={form.smtp_pass} onChange={e => ff({ smtp_pass: e.target.value })}
                    placeholder={cfg.smtp_pass_set ? 'Dejar vacío para mantener' : 'App password o contraseña SMTP'}
                    style={inputSt} />
                </Field>
              </div>
            </div>
          </Section>

          <Section title="Red interna (bypass de login)" icon="lan">
            <Field label="IPs o prefijos de red interna (separados por coma)">
              <input value={form.trusted_ips} onChange={e => ff({ trusted_ips: e.target.value })}
                placeholder="ej. 172.27.,192.168.1.,10.0." style={inputSt} />
            </Field>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6 }}>Usuarios desde estas redes entran sin OAuth. Vacío = siempre requiere login.</div>
          </Section>
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
          <Button variant="primary" onClick={async () => { setSaving(true); await onSave(form); setSaving(false); }}
            disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar credenciales'}
          </Button>
        </div>
      </div>
    </div>
  );
}
